// 赌场游戏数据状态管理
import { createContext, useContext } from "solid-js";
import { createStore, reconcile } from "solid-js/store";

import { postGetFrontMakers } from "@/api/clientApi/casino";
import { reqGetGameList } from "@/api/clientApi/front";
import { gclass_name_list } from "@/constants/constants";
import { NavsClickNewType } from "@/enum/component";
import { isNumeric } from "@/lib/utils";
import { GameNode } from "@/types/swagger/front";
import { GameNavItem } from "@locent/types";

export enum GameTag {
  top = "1",
  hot = "2",
  new = "3",
  champs = "4",
}

export interface queryGameParamsType {
  gclass: number | string; // 游戏分类ID
  page: number; // 当前页码
  page_size: number; // 每页数量
  sort: string; // 排序方式
  maker_tag: string; // 厂商标签（逗号分隔）
  tag?: string | number;
}

// 赌场状态接口定义
export interface casinoStoreType {
  currentTab: GameNavItem | undefined; // 当前选中的导航标签
  currentTabIndex: number; // 当前标签索引
  finish: boolean; // 是否已加载完所有数据
  loading: boolean; // 是否正在加载
  gameList: GameNode[]; // 游戏列表数据
  queryGameParams: queryGameParamsType; // 查询参数
  totalGame: number; // 总游戏数量
}

interface CasinoContextType {
  casinoStore: casinoStoreType;
  setCasinoStore: (state: Partial<casinoStoreType>) => void;
  updateQueryParam: <K extends keyof queryGameParamsType>(
    key: K,
    value: queryGameParamsType[K],
    shouldLoad?: boolean,
  ) => void;
  resetQueryGameParams: () => void;
  updatePageParams: (page: number) => void;
  updateGclassParams: (gclass: number | string) => void;
  updateMakerTagParams: (makerTag: string[], shouldLoad?: boolean) => void;
  updateSortParams: (sort: string, shouldLoad?: boolean) => void;
  updateGameList: (gameList: GameNode[]) => void;
  loadGameList: (isLoadMore?: boolean) => Promise<void>;
}
export type TabData = {
  clickType: NavsClickNewType;
  gclass: number;
};

// 默认配置常量
const DEFAULT_PAGE_SIZE = 42; // 默认每页显示42个游戏
const DEFAULT_QUERY_PARAMS: queryGameParamsType = {
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
  sort: "0",
  gclass: "",
  maker_tag: "",
};

export const getClickIdByTabName = (tabName: string) => {
  const result: TabData = {
    clickType: NavsClickNewType.home,
    gclass: 0,
  };

  const urlKeyList = gclass_name_list.map((el) => el.urlKey);
  urlKeyList.push(...["provider", "provider_games", "guess_you_like"]);
  if (!tabName || !urlKeyList.includes(tabName)) {
    return result;
  }

  switch (tabName) {
    case "home":
      result.clickType = NavsClickNewType.home;
      break;
    case "provider":
      result.clickType = NavsClickNewType.maker_list;
      break;
    case "provider_games":
      result.clickType = NavsClickNewType.maker_games;
      break;
    case "guess_you_like":
      result.clickType = NavsClickNewType.guess;
      break;
    default:
      result.clickType = NavsClickNewType.gclass;
      result.gclass =
        gclass_name_list.find((item) => item.urlKey === tabName)?.gclass || 0;
      break;
  }

  return result;
};

export const getGamePlatListBySelect = async (click_id: number) => {
  const res = await postGetFrontMakers({
    gclass: String(click_id),
    game_tag: "",
  });

  const result = res?.list?.map((item) => {
    return {
      ...item,
      label: item.name,
      value: item.tag,
    };
  });

  return result;
};

export const getUriTabName = (gclass: number, click_type?: number) => {
  let tabName = "";
  switch (click_type) {
    case NavsClickNewType.gclass:
      tabName =
        gclass_name_list.find((item) => item.gclass === gclass)?.urlKey || "";
      break;
    case NavsClickNewType.maker_list:
      tabName = "provider";
      break;
    case NavsClickNewType.maker_games:
      tabName = "provider_games";
      break;
    case NavsClickNewType.guess:
      tabName = "guess_you_like";
      break;
  }

  return tabName;
};

export const useCasino = () => {
  // 创建赌场状态存储
  const [casinoStore, setCasinoStore] = createStore<casinoStoreType>({
    currentTab: undefined,
    currentTabIndex: 0,
    finish: false,
    loading: false,
    gameList: [],
    totalGame: 0,
    queryGameParams: { ...DEFAULT_QUERY_PARAMS },
  });

  /**
   * 通用查询参数更新函数
   *
   * @param key 要更新的参数键
   * @param value 新的参数值
   * @param shouldLoad 是否立即加载数据
   */
  const updateQueryParam = <K extends keyof queryGameParamsType>(
    key: K,
    value: queryGameParamsType[K],
    shouldLoad = true,
  ) => {
    setCasinoStore("queryGameParams", (prev) => ({
      ...prev,
      [key]: value,
      page: 1, // 重置页码到第1页
    }));
    if (shouldLoad) loadGameList();
  };

  /** 重置查询参数到默认状态 */
  const resetQueryGameParams = () => {
    setCasinoStore("queryGameParams", reconcile({ ...DEFAULT_QUERY_PARAMS }));
  };

  /**
   * 更新页码参数（用于分页加载）
   *
   * @param page 新的页码
   */
  const updatePageParams = (page: number) => {
    setCasinoStore("queryGameParams", (prev) => ({ ...prev, page }));
  };

  /**
   * 更新游戏分类参数
   *
   * @param gclass 游戏分类ID
   */
  const updateGclassParams = (gclass: number | string) => {
    setCasinoStore("queryGameParams", (prev) => ({
      ...prev,
      gclass,
      maker_tag: "", // 清空厂商标签
      sort: "0", // 重置排序
      page: 1, // 重置页码
    }));
    loadGameList();
  };

  /**
   * 更新厂商标签参数
   *
   * @param makerTag 厂商标签数组
   * @param shouldLoad 是否立即加载数据
   */
  const updateMakerTagParams = (makerTag: string[], shouldLoad = true) =>
    updateQueryParam("maker_tag", makerTag.join(","), shouldLoad);

  /**
   * 更新排序参数
   *
   * @param sort 排序方式
   * @param shouldLoad 是否立即加载数据
   */
  const updateSortParams = (sort: string, shouldLoad = true) =>
    updateQueryParam("sort", sort, shouldLoad);

  /**
   * 更新游戏列表
   *
   * @param gameList 游戏列表
   */
  const updateGameList = (gameList: GameNode[]) => {
    setCasinoStore("gameList", gameList);
  };

  /**
   * 加载游戏列表数据
   *
   * @param isLoadMore 是否为加载更多模式
   */
  const loadGameList = async (isLoadMore = false) => {
    setCasinoStore("loading", true);
    try {
      const res = await reqGetGameList(casinoStore.queryGameParams);
      const newGameList = res?.list || [];
      const totalCount = res?.total || 0;

      if (isLoadMore) {
        // 加载更多模式：判断是否还有更多数据
        if (casinoStore.gameList.length === casinoStore.totalGame) {
          setCasinoStore("finish", true); // 标记已加载完所有数据
          return;
        }

        updatePageParams(casinoStore.queryGameParams.page + 1);
        setCasinoStore("gameList", (prev) => [...prev, ...newGameList]); // 追加数据
      } else {
        // 新搜索模式：替换现有数据
        updatePageParams(casinoStore.queryGameParams.page + 1);
        setCasinoStore("gameList", newGameList);
        setCasinoStore("totalGame", totalCount);
        setCasinoStore("finish", false); // 重置完成状态
      }
    } finally {
      setCasinoStore("loading", false);
    }
  };

  return {
    casinoStore,
    setCasinoStore,
    updateQueryParam,
    resetQueryGameParams,
    updatePageParams,
    updateGclassParams,
    updateMakerTagParams,
    updateSortParams,
    updateGameList,
    loadGameList,
  };
};

export const casinoContext = createContext<CasinoContextType>();

export const useCosinoContext = () => {
  const ctx = useContext(casinoContext);
  if (!ctx)
    throw new Error("CasinoContext must be used inside a <CasinoProvider>");
  return ctx;
};
